import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { fileUrl } from '../lib/fileUrl';
import {
  ClipboardList, Plus, Save, Trash2, Pencil, Upload, Download, Paperclip, FileText, X
} from 'lucide-react';
import { Pagination, usePagination } from './Pagination';

const API_URL = '';

const STATUSES = ['Draft', "For Client's approval and Payment", 'Paid for Execution', 'Executed'];

const STATUS_COLORS = {
  'Draft': 'bg-gray-100 text-gray-700',
  "For Client's approval and Payment": 'bg-yellow-100 text-yellow-700',
  'Paid for Execution': 'bg-blue-100 text-blue-700',
  'Executed': 'bg-emerald-100 text-emerald-700',
};

/**
 * ChangeOrdersTab
 * - mode='construction' → can create/edit items (no cost field), upload item photos
 * - mode='finance'      → can set cost per item, upload approved-form + proof-of-payment, mark Executed
 */
export default function ChangeOrdersTab({ mode = 'construction' }) {
  const isFinance = mode === 'finance';
  const [orders, setOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    try {
      const [coRes, prjRes] = await Promise.all([
        axios.get(`${API_URL}/api/change-orders`),
        axios.get(`${API_URL}/api/projects`),
      ]);
      setOrders(coRes.data || []);
      setProjects(prjRes.data || []);
    } catch { toast.error('Failed to load change orders'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pageData = usePagination(orders, 20);

  const blankItem = () => ({ item_number: 1, scope_of_works: '', qty: 0, unit: '', unit_cost: 0, price: 0, photo_url: null, photo_file: null });

  const openNew = () => {
    setEditing(null);
    setForm({
      project_id: projects[0]?.id || '',
      date_of_request: new Date().toISOString().slice(0, 10),
      requested_by: 'By Client',
      contractor: 'Desq Construction',
      original_scope: '',
      actual_scope: '',
      impact_type: 'Additive',
      prepared_by: '',
      items: [blankItem()],
    });
    setShowDialog(true);
  };

  const openEdit = (co) => {
    setEditing(co);
    setForm({
      project_id: co.project_id,
      date_of_request: co.date_of_request || '',
      requested_by: co.requested_by || 'By Client',
      contractor: co.contractor || '',
      original_scope: co.original_scope || '',
      actual_scope: co.actual_scope || '',
      impact_type: co.impact_type || 'Additive',
      prepared_by: co.prepared_by || '',
      items: (co.items || []).map((it, idx) => ({ ...it, item_number: idx + 1 })),
    });
    setShowDialog(true);
  };

  const closeDialog = () => { setShowDialog(false); setEditing(null); setForm(null); };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...blankItem(), item_number: f.items.length + 1 }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx).map((it, i) => ({ ...it, item_number: i + 1 })) }));
  const updateItem = (idx, key, value) => setForm(f => ({
    ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: value } : it)
  }));

  const handleSave = async () => {
    if (!form.project_id) return toast.error('Pick a project');
    if (!form.date_of_request) return toast.error('Date of request required');
    if (!form.items.length) return toast.error('Add at least one item');
    setSaving(true);
    try {
      const payload = {
        project_id: form.project_id,
        date_of_request: form.date_of_request,
        requested_by: form.requested_by,
        contractor: form.contractor || '',
        original_scope: form.original_scope || '',
        actual_scope: form.actual_scope || '',
        impact_type: form.impact_type || 'Additive',
        prepared_by: form.prepared_by || '',
        items: form.items.map((it, i) => ({
          item_number: i + 1,
          scope_of_works: it.scope_of_works || '',
          qty: parseFloat(it.qty) || 0,
          unit: it.unit || '',
          unit_cost: parseFloat(it.unit_cost) || 0,
          price: parseFloat(it.price) || 0,
          photo_url: it.photo_url || null,
        })),
      };
      let coId = editing?.id;
      if (editing) {
        await axios.put(`${API_URL}/api/change-orders/${editing.id}`, payload);
      } else {
        const res = await axios.post(`${API_URL}/api/change-orders`, payload);
        coId = res.data?.id;
      }
      // Upload any pending photos staged during creation/edit
      for (let i = 0; i < form.items.length; i++) {
        const it = form.items[i];
        if (it.photo_file && coId) {
          const fd = new FormData();
          fd.append('file', it.photo_file);
          try {
            await axios.post(`${API_URL}/api/change-orders/${coId}/upload?doc_type=item_photo&item_index=${i}`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          } catch {
            toast.error(`Failed to upload photo for item ${i + 1}`);
          }
        }
      }
      toast.success(editing ? 'Change order updated' : 'Change order created');
      closeDialog();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this change order? Files will also be removed.')) return;
    try {
      await axios.delete(`${API_URL}/api/change-orders/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await axios.put(`${API_URL}/api/change-orders/${id}`, { status });
      toast.success(`Status: ${res.data.status}`);
      load();
    } catch { toast.error('Failed to update status'); }
  };

  const handleUpload = async (coId, docType, file, itemIndex = null) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    const params = new URLSearchParams({ doc_type: docType });
    if (itemIndex !== null) params.set('item_index', String(itemIndex));
    try {
      const res = await axios.post(`${API_URL}/api/change-orders/${coId}/upload?${params}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded. Status: ${res.data.status}`);
      load();
      // If dialog open for this CO, refresh form state from new data
      if (editing && editing.id === coId) {
        const fresh = await axios.get(`${API_URL}/api/change-orders/${coId}`);
        setEditing(fresh.data);
        setForm(f => ({ ...f, items: (fresh.data.items || []).map((it, i) => ({ ...it, item_number: i + 1 })) }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const handleExportPdf = async (co) => {
    try {
      const res = await axios.get(`${API_URL}/api/change-orders/${co.id}/export-pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ChangeOrder_${co.control_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF export failed'); }
  };

  const lineTotal = (it) => parseFloat(it.price) || 0;
  const fmtPhp = (n) => `₱${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Card className="glass-effect border-border/40">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-primary" /> Change Orders {isFinance && <span className="text-xs text-muted-foreground">(Finance view — set cost & upload approved form / proof of payment)</span>}</CardTitle>
          {!isFinance && (
            <Button onClick={openNew} data-testid="btn-new-change-order"><Plus className="w-4 h-4 mr-1" /> New Change Order</Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : orders.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No change orders yet.</p>
        ) : (
          <>
            <div className="space-y-3">
              {pageData.items.map(co => (
                <div key={co.id} className="border border-border/40 rounded-lg p-3 bg-muted/10" data-testid={`co-row-${co.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-semibold text-primary">{co.control_number}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[co.status] || ''}`} data-testid={`co-status-${co.id}`}>{co.status}</span>
                        <span className="text-xs text-muted-foreground">{co.project_name}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {co.date_of_request} · Requested {co.requested_by} · {(co.items || []).length} item(s) · Total <span className="font-semibold text-foreground">{fmtPhp(co.total_amount)}</span>
                      </div>
                      {(co.client_name || co.project_address) && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                          {co.client_name && <span><span className="opacity-70">Client:</span> <span className="text-foreground/90 font-medium">{co.client_name}</span></span>}
                          {co.project_address && <span><span className="opacity-70">Location:</span> {co.project_address}</span>}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleExportPdf(co)} data-testid={`btn-co-export-${co.id}`}>
                        <Download className="w-3 h-3 mr-1" /> PDF
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openEdit(co)} data-testid={`btn-co-edit-${co.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> {isFinance ? 'Set Cost' : 'Edit'}
                      </Button>
                      {!isFinance && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => handleDelete(co.id)} data-testid={`btn-co-delete-${co.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Finance: status transitions + attachment slots */}
                  {isFinance && (
                    <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-border/30">
                      {co.status === 'Draft' && (
                        <Button size="sm" className="h-7 text-xs bg-yellow-500 hover:bg-yellow-600" onClick={() => handleStatusChange(co.id, "For Client's approval and Payment")} data-testid={`btn-co-submit-${co.id}`}>
                          Submit to Client
                        </Button>
                      )}
                      {co.status === 'Paid for Execution' && (
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => handleStatusChange(co.id, 'Executed')} data-testid={`btn-co-execute-${co.id}`}>
                          Mark Executed
                        </Button>
                      )}
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        Auto → &quot;Paid for Execution&quot; when both attachments present
                      </span>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                    <AttachmentChip co={co} docType="approved_form" label="Approved CO Form" isFinance={isFinance} onUpload={handleUpload} fileInputs={fileInputs} />
                    <AttachmentChip co={co} docType="proof_of_payment" label="Proof of Payment" isFinance={isFinance} onUpload={handleUpload} fileInputs={fileInputs} />
                  </div>
                </div>
              ))}
            </div>
            <Pagination page={pageData.page} totalPages={pageData.totalPages} setPage={pageData.setPage} total={pageData.total} pageSize={pageData.pageSize} label="change orders" testIdPrefix="co-pg" />
          </>
        )}
      </CardContent>

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.control_number}` : 'New Change Order'}</DialogTitle>
            <DialogDescription>
              {isFinance ? 'Set item prices and review the change order details.' : 'Fill in the change order details, scope of works and items.'}
            </DialogDescription>
          </DialogHeader>
          {form && (() => {
            const proj = projects.find(p => p.id === form.project_id);
            const formTotal = form.items.reduce((s, it) => s + lineTotal(it), 0);
            const sectionLabel = "text-[11px] font-bold uppercase tracking-wide bg-muted/50 px-2 py-1 rounded";
            return (
              <div className="space-y-4">
                {/* ===== Header block ===== */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Project Name *</Label>
                    <select value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60" disabled={!!editing || isFinance} data-testid="co-form-project">
                      <option value="">Select project…</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}{p.project_code ? ` (${p.project_code})` : ''}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input value={proj?.project_address || ''} disabled className="bg-muted/40" data-testid="co-form-location" />
                  </div>
                  <div>
                    <Label>Change Order No.</Label>
                    <Input value={editing?.control_number || 'Auto-generated on create'} disabled className="bg-muted/40 font-mono" data-testid="co-form-control-number" />
                  </div>
                  <div>
                    <Label>Date Issued *</Label>
                    <Input type="date" value={form.date_of_request} onChange={e => setForm({ ...form, date_of_request: e.target.value })} disabled={isFinance} data-testid="co-form-date" />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Input value={editing?.status || 'Draft'} disabled className="bg-muted/40" data-testid="co-form-status" />
                  </div>
                  <div>
                    <Label>Requested By</Label>
                    <select value={form.requested_by} onChange={e => setForm({ ...form, requested_by: e.target.value })}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60" disabled={isFinance} data-testid="co-form-requested-by">
                      <option value="By Client">By Client</option>
                      <option value="By Contractor">By Contractor</option>
                    </select>
                  </div>
                </div>

                {/* ===== Parties ===== */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Client&apos;s Name</Label>
                    <Input value={proj?.client_name || ''} disabled className="bg-muted/40" data-testid="co-form-client" />
                  </div>
                  <div>
                    <Label>Contractor</Label>
                    <Input value={form.contractor} onChange={e => setForm({ ...form, contractor: e.target.value })} placeholder="e.g., Desq Construction" disabled={isFinance} data-testid="co-form-contractor" />
                  </div>
                </div>

                {/* ===== Description of Change ===== */}
                <div className="space-y-2">
                  <div className={sectionLabel}>Description of Change</div>
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label>Original Scope</Label>
                      <Textarea value={form.original_scope} onChange={e => setForm({ ...form, original_scope: e.target.value })} placeholder="Original scope of works…" className="min-h-[60px]" disabled={isFinance} data-testid="co-form-original-scope" />
                    </div>
                    <div>
                      <Label>Actual Scope</Label>
                      <Textarea value={form.actual_scope} onChange={e => setForm({ ...form, actual_scope: e.target.value })} placeholder="Revised / actual scope of works…" className="min-h-[60px]" disabled={isFinance} data-testid="co-form-actual-scope" />
                    </div>
                  </div>
                </div>

                {/* ===== Impact on Contract ===== */}
                <div className="space-y-2">
                  <div className={sectionLabel}>Impact on Contract</div>
                  <div className="flex items-center gap-6">
                    {(() => {
                      const addChecked = form.impact_type === 'Additive' || form.impact_type === 'Both';
                      const dedChecked = form.impact_type === 'Deductive' || form.impact_type === 'Both';
                      const recompute = (add, ded) => {
                        let v = '';
                        if (add && ded) v = 'Both';
                        else if (add) v = 'Additive';
                        else if (ded) v = 'Deductive';
                        setForm({ ...form, impact_type: v });
                      };
                      return (
                        <>
                          <label className={`flex items-center gap-2 text-sm ${isFinance ? 'opacity-60' : 'cursor-pointer'}`}>
                            <input type="checkbox" checked={addChecked} disabled={isFinance}
                              onChange={e => !isFinance && recompute(e.target.checked, dedChecked)}
                              data-testid="co-form-impact-additive" />
                            Additive
                          </label>
                          <label className={`flex items-center gap-2 text-sm ${isFinance ? 'opacity-60' : 'cursor-pointer'}`}>
                            <input type="checkbox" checked={dedChecked} disabled={isFinance}
                              onChange={e => !isFinance && recompute(addChecked, e.target.checked)}
                              data-testid="co-form-impact-deductive" />
                            Deductive
                          </label>
                        </>
                      );
                    })()}
                  </div>

                  {/* Items table */}
                  <div className="mt-1 border border-border/40 rounded overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 text-[11px] font-semibold uppercase text-muted-foreground bg-muted/40 px-2 py-1.5 border-b border-border/40">
                      <div className="col-span-1">Item No.</div>
                      <div className="col-span-3">Scope of Works</div>
                      <div className="col-span-1 text-center">Qty</div>
                      <div className="col-span-1">Unit</div>
                      <div className="col-span-2 text-right">Unit Cost (₱)</div>
                      <div className="col-span-2 text-right">Price (₱)</div>
                      <div className="col-span-2 text-center">Actions</div>
                    </div>
                    {form.items.map((it, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 px-2 py-1.5 items-center border-b border-border/20" data-testid={`co-item-row-${idx}`}>
                        <div className="col-span-1 font-medium text-xs">{idx + 1}</div>
                        <Textarea className="col-span-3 min-h-[36px] text-xs" placeholder="Scope of works" value={it.scope_of_works} onChange={e => updateItem(idx, 'scope_of_works', e.target.value)} disabled={isFinance} data-testid={`co-item-scope-${idx}`} />
                        <Input type="number" step="0.01" className="col-span-1 h-8 text-xs text-center" value={it.qty} onChange={e => updateItem(idx, 'qty', e.target.value)} disabled={isFinance} data-testid={`co-item-qty-${idx}`} />
                        <Input className="col-span-1 h-8 text-xs" placeholder="sqm" value={it.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} disabled={isFinance} data-testid={`co-item-unit-${idx}`} />
                        <Input type="number" step="0.01" className="col-span-2 h-8 text-xs text-right" value={it.unit_cost} onChange={e => updateItem(idx, 'unit_cost', e.target.value)} disabled={!isFinance} data-testid={`co-item-unit-cost-${idx}`} />
                        <Input type="number" step="0.01" className="col-span-2 h-8 text-xs text-right" value={it.price} onChange={e => updateItem(idx, 'price', e.target.value)} disabled={!isFinance} data-testid={`co-item-price-${idx}`} />
                        <div className="col-span-2 flex items-center justify-center gap-1">
                          {it.photo_url ? (
                            <a href={fileUrl(it.photo_url)} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-[10px] inline-flex items-center gap-0.5">
                              <Paperclip className="w-3 h-3" /> Photo
                            </a>
                          ) : it.photo_file ? (
                            <span className="text-[10px] text-green-600 inline-flex items-center gap-0.5"><Paperclip className="w-3 h-3" /> staged</span>
                          ) : null}
                          {!isFinance && (
                            <>
                              <input type="file" accept=".pdf,image/*" className="hidden" id={`co-item-photo-input-${idx}`}
                                onChange={e => {
                                  const f = e.target.files?.[0];
                                  if (!f) return;
                                  if (editing) handleUpload(editing.id, 'item_photo', f, idx);
                                  else updateItem(idx, 'photo_file', f);
                                }} />
                              <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5" title="Attach photo"
                                onClick={() => document.getElementById(`co-item-photo-input-${idx}`)?.click()} data-testid={`btn-co-item-upload-${idx}`}>
                                <Upload className="w-3 h-3" />
                              </Button>
                              {form.items.length > 1 && (
                                <button onClick={() => removeItem(idx)} className="text-destructive hover:bg-destructive/10 rounded p-1" data-testid={`btn-co-item-remove-${idx}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                    {!isFinance && (
                      <button onClick={addItem} className="w-full text-xs py-2 hover:bg-muted/30 text-primary flex items-center justify-center gap-1" data-testid="btn-co-add-item">
                        <Plus className="w-3 h-3" /> Add Item
                      </button>
                    )}
                    {/* Total */}
                    <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-t border-border/40">
                      <span className="text-xs font-bold uppercase">Total Amount</span>
                      <span className="font-mono font-bold" data-testid="co-form-total">{fmtPhp(formTotal)}</span>
                    </div>
                  </div>
                </div>

                {/* ===== Prepared by ===== */}
                <div>
                  <Label>Prepared by</Label>
                  <Input value={form.prepared_by} onChange={e => setForm({ ...form, prepared_by: e.target.value })} placeholder="Name of preparer" disabled={isFinance} data-testid="co-form-prepared-by" />
                </div>

                {!editing && !isFinance && (
                  <p className="text-[10px] text-muted-foreground">Click <Upload className="w-2.5 h-2.5 inline" /> beside each item to attach a photo. Prices are set by Finance. Approved Form &amp; Proof of Payment are uploaded later from Finance.</p>
                )}
                <div className="flex justify-end gap-2 pt-3 border-t border-border/40">
                  <Button variant="outline" onClick={closeDialog}>Cancel</Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="btn-co-save"><Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : (editing ? 'Update' : 'Create')}</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function AttachmentChip({ co, docType, label, isFinance, onUpload, fileInputs }) {
  const url = docType === 'approved_form' ? co.approved_form_url : co.proof_of_payment_url;
  const canEdit = isFinance; // only Finance manages these
  const key = `${co.id}-${docType}`;
  return (
    <div className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border/40 bg-card">
      <FileText className="w-3 h-3 text-muted-foreground" />
      <span className="text-[11px] text-muted-foreground">{label}:</span>
      {url ? (
        <a href={fileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-[11px]" data-testid={`co-attach-link-${docType}`}>View</a>
      ) : (
        <span className="text-[11px] text-muted-foreground italic">none</span>
      )}
      {canEdit && (
        <>
          <input ref={el => (fileInputs.current[key] = el)} type="file" accept=".pdf,image/*" className="hidden"
            onChange={e => onUpload(co.id, docType, e.target.files?.[0])} />
          <Button size="sm" variant="ghost" className="h-5 px-1 text-[10px]" onClick={() => fileInputs.current[key]?.click()} data-testid={`co-attach-btn-${docType}`}>
            {url ? <Pencil className="w-3 h-3" /> : <Upload className="w-3 h-3" />}
          </Button>
        </>
      )}
    </div>
  );
}
